/**
 * 搜索。
 *
 * 只有帖子搜索是无限滚动；用户与话题按匹配质量排序、只有一页
 * （后端不给 next_cursor，理由见 backend/app/services/search.py）。
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { searchApi } from '@/api/endpoints'
import { keys } from '@/api/keys'
import type { Page, Post, SearchType, TimelineKind } from '@/api/types'
import { useDebounce } from '@/hooks/useDebounce'

export function useSearchPosts(q: string, kind: TimelineKind = 'all') {
  return useInfiniteQuery({
    queryKey: [...keys.search(q, 'posts'), kind],
    queryFn: ({ pageParam }) => searchApi.posts(q, pageParam, 20, kind),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: Page<Post>) => last.next_cursor ?? undefined,
    enabled: q.length > 0,
  })
}

export function useSearchUsers(q: string) {
  return useQuery({
    queryKey: keys.search(q, 'users'),
    queryFn: () => searchApi.users(q).then((r) => r.items),
    enabled: q.length > 0,
  })
}

export function useSearchHashtags(q: string) {
  return useQuery({
    queryKey: keys.search(q, 'hashtags'),
    queryFn: () => searchApi.hashtags(q).then((r) => r.items),
    enabled: q.length > 0,
  })
}

/** 搜索框的下拉提示。防抖 250ms，免得每敲一个字就打一次后端。 */
export function useTypeahead(raw: string) {
  const q = useDebounce(raw.trim(), 250)
  return useQuery({
    queryKey: keys.typeahead(q),
    queryFn: () => searchApi.typeahead(q),
    enabled: q.length > 0,
    staleTime: 60_000,
  })
}

export const SEARCH_TYPES: SearchType[] = ['posts', 'users', 'hashtags']
